const VISITOR_KEY = "ifc-viewer.analytics-visitor";
const START_KEY = "ifc-viewer.analytics-start";

const ANALYTICS_MODE = import.meta.env.VITE_ANALYTICS_MODE;
const ANALYTICS_URL = import.meta.env.VITE_ANALYTICS_URL || "";
const analyticsEnabled = ANALYTICS_MODE === "embedded" || ANALYTICS_MODE === "public";

function sessionId(): string {
  let id = readVisitorId();
  if (!id) {
    id = crypto.randomUUID();
    writeVisitorId(id);
  }
  if (!sessionStorage.getItem(START_KEY)) {
    sessionStorage.setItem(START_KEY, String(Date.now()));
  }
  return id;
}

function readVisitorId(): string | null {
  try {
    return localStorage.getItem(VISITOR_KEY);
  } catch {
    return sessionStorage.getItem(VISITOR_KEY);
  }
}

function writeVisitorId(id: string): void {
  try {
    localStorage.setItem(VISITOR_KEY, id);
  } catch {
    sessionStorage.setItem(VISITOR_KEY, id);
  }
}

export function sizeBucket(bytes: number): "lt10mb" | "10to100mb" | "gt100mb" {
  if (bytes < 10 * 1024 * 1024) return "lt10mb";
  if (bytes < 100 * 1024 * 1024) return "10to100mb";
  return "gt100mb";
}

function postEvent(body: string): void {
  if (!analyticsEnabled || !ANALYTICS_URL) return;
  if (ANALYTICS_MODE === "embedded") {
    void fetch(ANALYTICS_URL, {
      method: "POST",
      credentials: "include",
      headers: { "content-type": "application/json" },
      body,
      keepalive: true,
    }).catch(() => undefined);
    return;
  }
  void fetch(ANALYTICS_URL, {
    method: "POST",
    credentials: "omit",
    headers: { "content-type": "application/json" },
    body,
    keepalive: true,
  }).catch(() => undefined);
}

export function track(event: string, props: Record<string, string | number> = {}): void {
  if (!analyticsEnabled) return;
  postEvent(JSON.stringify({ sessionId: sessionId(), event, props }));
}

export function trackModelOpen(
  source: "cde" | "pc" | "presentation",
  extra?: { sizeBytes?: number; presentationId?: string },
): void {
  const props: Record<string, string | number> = { source };
  if (source === "pc" && extra?.sizeBytes) props.sizeBucket = sizeBucket(extra.sizeBytes);
  if (source === "presentation" && extra?.presentationId) props.presentationId = extra.presentationId;
  track("model_open", props);
}

export function trackTool(tool: string): void {
  track("tool_use", { tool });
}

let sessionEndSent = false;

function sendSessionEnd(): void {
  if (sessionEndSent || !analyticsEnabled || !ANALYTICS_URL) return;
  sessionEndSent = true;
  const start = Number(sessionStorage.getItem(START_KEY) || Date.now());
  const durationSec = Math.max(0, Math.min(86400, Math.round((Date.now() - start) / 1000)));
  const body = JSON.stringify({
    sessionId: sessionId(),
    event: "session_end",
    props: { durationSec },
  });
  if (ANALYTICS_MODE === "embedded") {
    const sent = navigator.sendBeacon(ANALYTICS_URL, new Blob([body], { type: "application/json" }));
    if (sent) return;
  }
  postEvent(body);
}

export function initAnalytics(): void {
  if (!analyticsEnabled) return;
  sessionId();
  track("pageview");
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") sendSessionEnd();
  });
  window.addEventListener("pagehide", sendSessionEnd);
}
